import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Instagram, Youtube, Mail, ArrowUpRight, Send } from 'lucide-react';
import { track } from '../lib/analytics';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [startTs, setStartTs] = useState(Date.now());
  const [status, setStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setStartTs(Date.now()); }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });
    if (!email) return;
    if (honeypot) return; // silent bot trap
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          honeypot,
          elapsedMs: Date.now() - startTs,
          source: 'footer',
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not subscribe you right now.');
      }
      setStatus({ type: 'success', message: 'You are in. Watch your inbox for a confirmation.' });
      track('newsletter_signup', { location: 'footer' });
      setEmail('');
      setStartTs(Date.now());
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Could not subscribe you right now.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-ink text-[var(--gh-cream)] relative overflow-hidden"
    >
      {/* Big wordmark backdrop */}
      <div className="container-x pt-20 pb-12 relative">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
              Growth Hacking
            </h3>
            <p className="mt-4 text-[15px] text-white/60 max-w-md leading-relaxed">
              Boutique email-marketing studio turning subscriber lists into predictable
              revenue engines. Kit.com · ActiveCampaign · ManyChat experts.
            </p>

            <form
              onSubmit={handleSubscribe}
              data-testid="newsletter-form"
              className="mt-8 max-w-md"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">
                Subscribe to the newsletter
              </p>
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full p-1.5 pl-5">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-transparent outline-none text-sm placeholder-white/40 text-white"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="newsletter-submit-btn"
                  className="inline-flex items-center gap-1 bg-[var(--gh-orange)] hover:bg-[var(--gh-orange-deep)] disabled:opacity-60 text-white text-sm font-semibold rounded-full px-4 py-2 transition-colors"
                >
                  {submitting ? 'Adding…' : 'Subscribe'} <Send className="w-4 h-4" />
                </button>
              </div>
              {/* honeypot */}
              <input
                type="text"
                name="company"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
              />
              {status.type === 'success' && (
                <p className="mt-3 text-xs text-[var(--gh-orange)]" data-testid="newsletter-success">
                  {status.message}
                </p>
              )}
              {status.type === 'error' && (
                <p className="mt-3 text-xs text-red-400" data-testid="newsletter-error">
                  {status.message}
                </p>
              )}
            </form>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">Services</h4>
              <ul className="space-y-3 text-sm text-white/80">
                <li><Link to="/services" className="hover:text-[var(--gh-orange)] transition-colors">Email System Build</Link></li>
                <li><Link to="/services" className="hover:text-[var(--gh-orange)] transition-colors">Deliverability Fix</Link></li>
                <li><Link to="/services" className="hover:text-[var(--gh-orange)] transition-colors">Migration & Cleanup</Link></li>
                <li><Link to="/services" className="hover:text-[var(--gh-orange)] transition-colors">Newsletter Monetization</Link></li>
                <li><Link to="/services" className="hover:text-[var(--gh-orange)] transition-colors">Retainer</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">Studio</h4>
              <ul className="space-y-3 text-sm text-white/80">
                <li><Link to="/about" className="hover:text-[var(--gh-orange)] transition-colors">About</Link></li>
                <li><Link to="/case-studies" className="hover:text-[var(--gh-orange)] transition-colors">Case Studies</Link></li>
                <li><Link to="/newsletter" className="hover:text-[var(--gh-orange)] transition-colors">Newsletter</Link></li>
                <li><Link to="/blog" className="hover:text-[var(--gh-orange)] transition-colors">Field Notes</Link></li>
                <li><Link to="/contact" className="hover:text-[var(--gh-orange)] transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">Connect</h4>
              <ul className="space-y-3 text-sm text-white/80">
                <li>
                  <a
                    href="https://tidycal.com/akashtiwari/30-minute-meeting"
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:text-[var(--gh-orange)] transition-colors"
                    data-testid="footer-book-call-link"
                  >
                    Book a call <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </li>
                <li><Link to="/contact" className="hover:text-[var(--gh-orange)] transition-colors">Send a message</Link></li>
              </ul>
              <div className="mt-5 flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/in/akashtiwarii/"
                  target="_blank" rel="noopener noreferrer"
                  data-testid="footer-linkedin"
                  aria-label="LinkedIn"
                  className="w-9 h-9 rounded-full border border-white/15 hover:border-[var(--gh-orange)] hover:text-[var(--gh-orange)] flex items-center justify-center transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a
                  href="https://www.instagram.com/growthhackeng/"
                  target="_blank" rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  aria-label="Instagram"
                  className="w-9 h-9 rounded-full border border-white/15 hover:border-[var(--gh-orange)] hover:text-[var(--gh-orange)] flex items-center justify-center transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://x.com/akashtiwarii"
                  target="_blank" rel="noopener noreferrer"
                  data-testid="footer-x"
                  aria-label="X"
                  className="w-9 h-9 rounded-full border border-white/15 hover:border-[var(--gh-orange)] hover:text-[var(--gh-orange)] flex items-center justify-center transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href="https://www.youtube.com/@akash.growth"
                  target="_blank" rel="noopener noreferrer"
                  data-testid="footer-youtube"
                  aria-label="YouTube"
                  className="w-9 h-9 rounded-full border border-white/15 hover:border-[var(--gh-orange)] hover:text-[var(--gh-orange)] flex items-center justify-center transition-colors"
                >
                  <Youtube className="w-4 h-4" />
                </a>
                <Link
                  to="/contact"
                  data-testid="footer-mail"
                  aria-label="Contact"
                  className="w-9 h-9 rounded-full border border-white/15 hover:border-[var(--gh-orange)] hover:text-[var(--gh-orange)] flex items-center justify-center transition-colors"
                >
                  <Mail className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/45">
            © {new Date().getFullYear()} Growth Hacking · Crafted by Akash Tiwari
          </p>
          <p className="text-xs text-white/45">
            growthhackeng.com · akash@growthhackeng.com
          </p>
        </div>
      </div>

      {/* Oversized faded brand wordmark */}
      <div
        aria-hidden
        className="pointer-events-none select-none absolute -bottom-12 left-0 right-0 text-center font-display font-extrabold text-[18vw] leading-none text-white/[0.04] tracking-tighter"
      >
        growthhacking
      </div>
    </footer>
  );
};

export default Footer;
